import { useMemo } from 'react';
import { useGetCustomVirtualBackgroundsQuery } from '../../services/branding/operations.enhanced';
import {
  BrandedVirtualBackgroundVariant,
  CustomVirtualBackgroundSource,
} from '../../services/graphQlTypes.generated';
import { VirtualBackgroundGroups } from '../../components/media/virtualBackground/constants';
import { useIsBrandedVirtualBackgroundEnabled } from './useIsBrandedVirtualBackgroundEnabled';

const BRANDED_INDEX_BY_VARIANT: Record<
  BrandedVirtualBackgroundVariant,
  number
> = {
  [BrandedVirtualBackgroundVariant.LogoBrick]: 100,
  [BrandedVirtualBackgroundVariant.IconBrick]: 101,
};

const VARIANT_METADATA: Record<
  BrandedVirtualBackgroundVariant,
  { name: string; title: string }
> = {
  [BrandedVirtualBackgroundVariant.LogoBrick]: {
    name: 'Logo pattern',
    title: 'virtualBackground.options.customLogoBrick',
  },
  [BrandedVirtualBackgroundVariant.IconBrick]: {
    name: 'Icon pattern',
    title: 'virtualBackground.options.customIconBrick',
  },
};

export const useBrandedVirtualBackgrounds = () => {
  const isEnabled = useIsBrandedVirtualBackgroundEnabled();
  const { data } = useGetCustomVirtualBackgroundsQuery(undefined, {
    skip: !isEnabled,
  });

  return useMemo(() => {
    if (!isEnabled) {
      return [];
    }

    const rows = data?.Tenant?.customVirtualBackgrounds ?? [];
    const branded = rows.filter(
      (row) =>
        row.source === CustomVirtualBackgroundSource.Branded &&
        row.brandedVariant !== null &&
        row.brandedVariant !== undefined,
    );

    return branded.map((row) => {
      const variant = row.brandedVariant as BrandedVirtualBackgroundVariant;
      return {
        index: BRANDED_INDEX_BY_VARIANT[variant],
        group: VirtualBackgroundGroups.Branded,
        name: VARIANT_METADATA[variant].name,
        filter: {
          type: 'backgroundReplacement' as const,
          backgroundImgUrl: row.url,
        },
        metadata: {
          title: VARIANT_METADATA[variant].title,
          thumbnail: row.url,
        },
      };
    });
  }, [data, isEnabled]);
};
